// Mocks for the Datastore integration tests
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Rx from 'rxjs';
import {
  DatastoreCollection,
  DatastoreCollectionType,
  DatastoreDocument,
  MapCoordinates,
  Reference,
  RequestStatus,
  StoreBackend,
  SuccessResponseData,
} from '..';
import { TestingCollection } from './types';

/**
 * MockObject is a fake model that can be extended with mock fields in order to
 * support the Datastore tests
 */
export interface MockObject {
  readonly id: number;
  readonly username: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly context?: { readonly type: string; readonly id: number };
  readonly isEmployer?: boolean;
  readonly rating: number;
  readonly location?: MapCoordinates;
}

export interface MockObjectGetResultApi {
  readonly mockObjects: { readonly [k in string]?: MockObject };
}

export interface MockObjectPostRawPayload {
  readonly name: string;
}

export function makeMockObject({
  id,
  username = 'mockObject',
  jobs = ['js', 'html', 'css'],
  rating = 10,
  location,
}: {
  readonly id: number;
  readonly username?: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly rating?: number;
  readonly location?: MapCoordinates;
}): MockObject {
  return {
    id,
    username,
    jobs,
    rating,
    ...(location ? { location } : {}),
  };
}

/**
 * Mock response from API must have `status` and `result` fields.
 * The request is originally made in freelancer-http.service, which is the
 * location where Angular's native HTTP methods are called.
 */
export function getMockObjectFetchResponse({
  ids,
  username = 'mockObject',
  jobs = ['js', 'html', 'css'],
  rating = 10,
}: {
  readonly ids: ReadonlyArray<number>;
  readonly username?: string | ((id: number) => string);
  readonly jobs?: ReadonlyArray<string>;
  readonly rating?: number;
}): SuccessResponseData<TestingCollection['Backend']['Fetch']['ReturnType']> {
  return {
    result: {
      mockObjects: ids.reduce(
        (acc, id) => ({
          ...acc,
          [id]: makeMockObject({
            id,
            username: typeof username === 'string' ? username : username(id),
            jobs,
            rating,
          }),
        }),
        {},
      ),
    },
    status: 'success',
  };
}

// Mock responses for the rest of the CRUD operations
export function getMockObjectPushResponse(): SuccessResponseData<
  TestingCollection['Backend']['Push']['ReturnType']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectSetResponse(): SuccessResponseData<
  TestingCollection['Backend']['Set']['ReturnType']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectUpdateResponse(): SuccessResponseData<
  TestingCollection['Backend']['Update']['ReturnType']
> {
  return {
    result: {},
    status: 'success',
  };
}

export function getMockObjectDeleteResponse(): SuccessResponseData<
  TestingCollection['Backend']['Delete']['ReturnType']
> {
  return {
    result: {},
    status: 'success',
  };
}

// api-http.service mock
@Injectable()
export class MockApiHttp {
  // inject Angular's HttpClient in place of our FreelancerHttp service
  constructor(private http: HttpClient) {}

  fetch() {
    return this.http.get('');
  }

  post() {
    return this.http.post('', {});
  }

  put() {
    return this.http.put('', {});
  }

  delete() {
    return this.http.delete('');
  }
}

export class DatastoreCollectionMock<
  C extends DatastoreCollectionType
> extends DatastoreCollection<C> {
  constructor(valueChanges$: Rx.Observable<ReadonlyArray<C['DocumentType']>>) {
    super(
      Rx.of({} as Reference<C>),
      {} as StoreBackend,
      Rx.of({} as RequestStatus<C>),
      valueChanges$,
      Rx.NEVER, // FIXME
    );
  }
}

export class DatastoreDocumentMock<
  C extends DatastoreCollectionType
> extends DatastoreDocument<C> {
  constructor(
    valueChanges$: Rx.Observable<C['DocumentType']>,
    status$: Rx.Observable<RequestStatus<C>> = Rx.of({ ready: true }),
  ) {
    super(
      Rx.of({} as Reference<C> & { readonly path: { readonly ids: [string] } }),
      {} as StoreBackend,
      status$,
      valueChanges$,
    );
  }
}
